import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { companyData } from '../mock';
import { ArrowRight, Shield, Zap, Users, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home = () => {
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <div ref={containerRef} className="overflow-hidden">
      {/* Hero Section with Kinetic Animation */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-[#D4EDF7] via-white to-[#D4EDF7] pt-20">
        {/* Animated Background Elements */}
        <motion.div 
          className="absolute inset-0 opacity-20"
          style={{ y }}
        >
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#004C97] rounded-full blur-3xl opacity-20" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#003366] rounded-full blur-3xl opacity-20" />
        </motion.div>

        <motion.div 
          className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center"
          style={{ opacity }}
        >
          {/* Masked Line-by-Line Reveal */}
          <div className="mb-8">
            {companyData.hero.title.split(' ').map((word, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ 
                  duration: 0.8, 
                  delay: index * 0.1,
                  ease: [0.22, 1, 0.36, 1]
                }}
                className="inline-block mr-4 text-5xl md:text-7xl lg:text-8xl font-bold text-[#004C97] mb-4"
              >
                {word}
              </motion.span>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-xl md:text-2xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed"
          >
            {companyData.hero.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link to="/services">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(0,76,151,0.3)' }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-4 bg-[#004C97] text-white rounded-lg font-semibold flex items-center gap-2 shadow-lg hover:bg-[#003366] transition-colors"
              >
                Explore Services
                <ArrowRight size={20} />
              </motion.button>
            </Link>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-4 bg-white text-[#004C97] rounded-lg font-semibold border-2 border-[#004C97] hover:bg-[#D4EDF7] transition-colors"
              >
                Get in Touch
              </motion.button>
            </Link>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="w-6 h-10 border-2 border-[#004C97] rounded-full flex items-start justify-center p-2"
            >
              <motion.div className="w-1.5 h-1.5 bg-[#004C97] rounded-full" />
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Why Choose Us Section */}
      <WhyChooseUs />

      {/* Slow Editorial Marquee */}
      <MarqueeSection />

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

const TrustedClients = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#004C97] mb-4">
            Trusted by Industry Leaders
          </h2>
          <p className="text-gray-600 text-lg">Delivering excellence for leading insurance companies</p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {companyData.clients.map((client, index) => (
            <motion.div
              key={client.name}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              className="flex items-center justify-center p-8 bg-gray-50 rounded-xl hover:bg-[#D4EDF7] transition-colors"
            >
              <img 
                src={client.logo} 
                alt={`${client.name} logo`}
                className="max-h-16 w-auto object-contain transition-all duration-300"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyChooseUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    {
      icon: Shield,
      title: "Insurance Expertise",
      description: "100% focus on insurance domain with deep industry knowledge"
    },
    {
      icon: Zap,
      title: "Agile & Responsive",
      description: "Quick turnaround times and adaptive to your changing needs"
    },
    {
      icon: Users,
      title: "Dedicated Team",
      description: "Highly skilled professionals with cross-functional expertise"
    },
    {
      icon: Award,
      title: "Proven Track Record",
      description: "Successful delivery for leading insurance companies"
    }
  ];

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-white to-[#D4EDF7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#004C97] mb-4">
            Why Choose Randovate Labs
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            We combine domain knowledge, technical expertise, and a client-first mindset
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all"
              >
                <div className="w-14 h-14 bg-[#D4EDF7] rounded-xl flex items-center justify-center mb-6">
                  <Icon className="text-[#004C97]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#004C97] mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const MarqueeSection = () => {
  const words = ['INNOVATION', 'INSURANCE', 'TECHNOLOGY', 'EXPERTISE', 'TRUST'];
  
  return (
    <section className="py-16 bg-[#004C97] overflow-hidden">
      <motion.div
        animate={{ x: ['0%', '-50%'] }}
        transition={{ 
          duration: 30, 
          repeat: Infinity, 
          ease: 'linear' 
        }}
        className="flex gap-16 whitespace-nowrap"
      >
        {[...words, ...words, ...words].map((word, index) => (
          <span
            key={index}
            className="text-6xl md:text-8xl font-bold text-[#D4EDF7] opacity-20"
          >
            {word}
          </span>
        ))}
      </motion.div>
    </section>
  );
};

const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="py-24 bg-[#D4EDF7]">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.8 }}
        className="max-w-4xl mx-auto px-6 lg:px-8 text-center"
      >
        <h2 className="text-4xl md:text-6xl font-bold text-[#004C97] mb-6">
          Ready to Transform Your Insurance Operations?
        </h2>
        <p className="text-xl text-gray-700 mb-10 leading-relaxed">
          Let's discuss how we can help you achieve your goals with our tailored solutions
        </p>
        <Link to="/contact">
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(0,76,151,0.3)' }}
            whileTap={{ scale: 0.98 }}
            className="px-10 py-5 bg-[#004C97] text-white rounded-lg font-semibold text-lg flex items-center gap-3 mx-auto shadow-xl hover:bg-[#003366] transition-colors"
          >
            Start a Conversation
            <ArrowRight size={24} />
          </motion.button>
        </Link>
      </motion.div>
    </section>
  );
};

export default Home;